import { auth } from './firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8002';

/**
 * Helper to get the Firebase ID token for the current user.
 */
async function getAuthToken() {
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken();
}

/**
 * Generic fetch wrapper that adds Authorization header.
 */
async function apiFetch(endpoint, options = {}) {
  const token = await getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `API error: ${response.status}`);
  }

  return await response.json();
}

export const bibleApi = {
  /**
   * Syncs the Firebase user with the backend DB on login/registration.
   * Returns the user profile: { user_id, firebase_uid, email, name, created_at }
   */
  syncUser: async () => {
    return await apiFetch('/bible/user', { method: 'POST' });
  },

  /**
   * Fetch notes for the current user scoped to a specific language and Bible version.
   * @param {string} language - e.g. "en"
   * @param {string} version_id - e.g. "kjv"
   */
  getNotes: async (language, version_id) => {
    return await apiFetch(`/bible/notes?language=${encodeURIComponent(language)}&version_id=${encodeURIComponent(version_id)}`);
  },

  /**
   * Create a new note.
   * @param {Object} noteData - { book, chapter, verse, content }
   */
  createNote: async (noteData) => {
    return await apiFetch('/bible/notes', {
      method: 'POST',
      body: JSON.stringify(noteData),
    });
  },

  /**
   * Get a specific note by ID.
   */
  getNote: async (noteId) => {
    return await apiFetch(`/bible/notes/${noteId}`);
  },

  /**
   * Update an existing note.
   * @param {number} noteId 
   * @param {Object} noteData - { book, chapter, verse, content }
   */
  updateNote: async (noteId, noteData) => {
    return await apiFetch(`/bible/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(noteData),
    });
  },

  /**
   * Delete a note.
   * @param {number} noteId 
   */
  deleteNote: async (noteId) => {
    return await apiFetch(`/bible/notes/${noteId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Fetch bookmarks for the current user scoped to a specific language and Bible version.
   */
  getBookmarks: async (language, version_id) => {
    return await apiFetch(`/bible/bookmarks?language=${encodeURIComponent(language)}&version_id=${encodeURIComponent(version_id)}`);
  },

  /**
   * Create a bookmark.
   * @param {Object} data - { book, chapter, verse, language, version_id }
   */
  createBookmark: async (data) => {
    return await apiFetch('/bible/bookmarks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a bookmark.
   * @param {number} bookmarkId
   */
  deleteBookmark: async (bookmarkId) => {
    return await apiFetch(`/bible/bookmarks/${bookmarkId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Fetch highlights for the current user scoped to a specific language and Bible version.
   */
  getHighlights: async (language, version_id) => {
    return await apiFetch(`/bible/highlights?language=${encodeURIComponent(language)}&version_id=${encodeURIComponent(version_id)}`);
  },

  /**
   * Create a highlight.
   * @param {Object} data - { book, chapter, verse, start_offset, end_offset, highlighted_text, color, language, version_id }
   */
  createHighlight: async (data) => {
    return await apiFetch('/bible/highlights', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a highlight.
   * @param {number} highlightId
   */
  deleteHighlight: async (highlightId) => {
    return await apiFetch(`/bible/highlights/${highlightId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get list of available Bible versions and their CDN URLs.
   */
  getVersions: async () => {
    // This would eventually call your backend on the Droplet
    // For now, returning a mock or calling the endpoint if it exists
    try {
      return await apiFetch('/bible/versions');
    } catch (e) {
      // Fallback for development if backend isn't updated yet
      return {
        versions: [
          { id: 'en-bbe', name: 'BBE', language: 'en', local: true },
          { id: 'en-kjv', name: 'King James Version', language: 'en', storage_path: 'bibles/en-kjv.json' },
          { id: 'en-web', name: 'World English Bible', language: 'en', storage_path: 'bibles/en-web.json' },
          { id: 'zh-cuv', name: 'Chinese Union Version', language: 'zh', storage_path: 'bibles/zh-cuv.json' }
        ]
      };
    }
  },

  /**
   * Health check
   */
  hello: async () => {
    return await apiFetch('/bible/hello');
  },

  /**
   * Fetch commentary for a specific book and chapter.
   * For non-English languages, tries the local translated endpoint first,
   * then falls back to HelloAO (English only).
   * @param {string} slug - Commentary slug (e.g. "matthew-henry")
   * @param {string} book - HelloAO 3-letter book code (e.g. "GEN")
   * @param {number} chapter - Chapter number
   * @param {string} language - Current UI language (e.g. "zh", "en")
   */
  /**
   * Semantic search — returns top-K Bible verses most relevant to a natural language query.
   * Falls back to [] if the server index is still loading.
   * @param {string} query - Natural language question or topic
   * @param {number} topK - Number of results (default 10)
   * @returns {Promise<Array<{book, chapter, verse, text, score}>>}
   */
  semanticSearch: async (query, topK = 10) => {
    try {
      const data = await apiFetch(
        `/bible/semantic-search?query=${encodeURIComponent(query)}&top_k=${topK}`
      );
      return data.results || [];
    } catch {
      return [];
    }
  },

  getCommentary: async (slug, book, chapter, language = 'en') => {
    const langTagMap = { en: 'en', zh: 'zh', 'zh-s': 'zh-s', fr: 'fr' };
    const langTag = langTagMap[language] || 'en';

    // Always try local droplet endpoint first (works for all languages including English)
    try {
      const localSlug = `${slug}-${langTag}`;
      const url = `${API_BASE_URL}/bible/commentary/local/${localSlug}/${book}/${chapter}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        if (Object.keys(data).length > 0) return { _local: true, data };
      }
    } catch { /* fall through to HelloAO */ }

    // Fall back to HelloAO if local not available
    const url = `https://bible.helloao.org/api/c/${slug}/${book}/${chapter}.json`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Commentary not found: ${response.status}`);
    return await response.json();
  }
};
